'use client';

import clsx from 'clsx';
import type { ReactNode } from 'react';

import { Card } from '../Card';
import { CMSSection } from '../CmsSection';
import type {
  CardContentItem,
  ContentGroupItem,
  PlaceholderServiceContentItem,
} from '../DoubleCard';
import { FreeTextContent } from '../FreeTextContent';
import { Heading, type HeadingData } from '../Heading';
import { getPlaceholderServiceRoutingProps } from '@/lib/cms/extraData';

import { PlaceholderService } from '../PlaceholderService';

import styles from './ThreeColumnStack.module.scss';

export interface ThreeColumnStackProps {
  /**
   * Collection of content items to render (legacy format)
   */
  itemsCollection?: {
    items: Array<ContentGroupItem | null>;
  } | null;
  /**
   * Direct items array (adapter format)
   * Takes precedence over itemsCollection if both provided
   */
  items?: Array<ContentGroupItem | null>;
  /** Content group type for styling context */
  type?: string | null;
  /** Extra data record for CMS hydration */
  extraDataRecord?: Record<string, unknown>;
  /** Optional className */
  className?: string;
}

/**
 * Get column class based on number of items
 */
function getColumnClass(itemCount: number): string {
  switch (itemCount) {
    case 2:
      return styles.col2;
    case 3:
      return styles.col3;
    case 4:
      return styles.col4;
    default:
      return styles.col3;
  }
}

/**
 * Default item renderer - renders based on __typename
 */
function renderItem(
  item: ContentGroupItem,
  extraDataRecord?: Record<string, unknown>,
  _type?: string | null
): ReactNode {
  switch (item.__typename) {
    case 'Heading':
      return <Heading Heading={item as HeadingData} />;
    case 'Card':
      return (
        <Card data={item} type={(item as CardContentItem).type as import('../Card').CardType} />
      );
    case 'ImageWithFocalPoint':
      return <Card data={item} type="MediaBanner" />;
    case 'ProductCard':
      return <Card data={item} type="MediaAndBottomText" />;
    case 'PlaceholderService': {
      const serviceItem = item as PlaceholderServiceContentItem;
      return (
        <PlaceholderService
          {...serviceItem}
          {...getPlaceholderServiceRoutingProps(extraDataRecord)}
          type={serviceItem.type}
        />
      );
    }
    case 'FreeTextContent':
      return <FreeTextContent freeTextContent={item.freeTextContent} />;
    default:
      return null;
  }
}

/**
 * ThreeColumnStack component
 *
 * Renders content items in a responsive 3-column grid.
 * Supports 2-4 items, adjusting column spans accordingly.
 *
 * @example
 * <ThreeColumnStack
 *   itemsCollection={{
 *     items: [
 *       { __typename: 'Card', sys: { id: '1' }, ... },
 *       { __typename: 'Card', sys: { id: '2' }, ... },
 *       { __typename: 'Card', sys: { id: '3' }, ... },
 *     ]
 *   }}
 * />
 */
export function ThreeColumnStack({
  itemsCollection,
  items: directItems,
  type,
  extraDataRecord,
  className,
}: ThreeColumnStackProps) {
  // Prefer direct items (adapter format), fallback to itemsCollection (legacy format)
  const rawItems = directItems ?? itemsCollection?.items ?? [];
  const items = rawItems.filter((item): item is ContentGroupItem => item !== null);

  if (items.length === 0) {
    return null;
  }

  const columnClass = getColumnClass(items.length);

  return (
    <CMSSection className={className} data-testid="ThreeColumnStackWrapper-section">
      <div className={styles.container}>
        <div className={styles.wrapper} data-testid="ThreeColumnStackWrapper">
          {items.map((item, index) => {
            const rendered = renderItem(item, extraDataRecord, type);
            if (!rendered) return null;

            return (
              <div
                key={item.sys.id}
                className={clsx(styles.col, columnClass)}
                data-testid={`ThreeColumnStackParameterCol-${index}`}
              >
                {rendered}
              </div>
            );
          })}
        </div>
      </div>
    </CMSSection>
  );
}

ThreeColumnStack.displayName = 'ThreeColumnStack';
